import { useState } from "react";
import { Check, UserPlus, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import type { Driver } from "@/hooks/useVehicles";

interface DriverAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  drivers: Driver[];
  assignedDriverIds: string[];
  onAssign: (driverId: string, isPrimary?: boolean) => Promise<void>;
  onUnassign: (driverId: string) => Promise<void>;
  isLoading?: boolean;
}

export function DriverAssignmentDialog({
  open,
  onOpenChange,
  drivers,
  assignedDriverIds,
  onAssign,
  onUnassign,
  isLoading = false,
}: DriverAssignmentDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const filteredDrivers = drivers.filter((driver) => {
    const fullName = `${driver.first_name} ${driver.last_name}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase()) || 
           driver.email.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleToggleDriver = async (driver: Driver) => {
    setPendingAction(driver.id);
    try {
      if (assignedDriverIds.includes(driver.id)) {
        await onUnassign(driver.id);
      } else {
        await onAssign(driver.id);
      }
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Assigner des conducteurs
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un conducteur..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Drivers list */}
          <ScrollArea className="h-[300px] pr-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredDrivers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {drivers.length === 0 
                  ? "Aucun conducteur créé" 
                  : "Aucun conducteur trouvé"}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredDrivers.map((driver) => {
                  const isAssigned = assignedDriverIds.includes(driver.id);
                  const isPending = pendingAction === driver.id;
                  const initials = `${driver.first_name.charAt(0)}${driver.last_name.charAt(0)}`.toUpperCase();
                  
                  return (
                    <div
                      key={driver.id}
                      className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                        isAssigned 
                          ? "bg-primary/5 border-primary/30" 
                          : "hover:bg-accent"
                      }`}
                      onClick={() => handleToggleDriver(driver)}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="text-sm bg-primary/10 text-primary">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">
                            {driver.first_name} {driver.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {driver.email}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {driver.license_categories && driver.license_categories.length > 0 && (
                          <div className="hidden sm:flex gap-1">
                            {driver.license_categories.slice(0, 2).map((cat) => (
                              <Badge key={cat} variant="outline" className="text-xs">
                                {cat}
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {isPending ? (
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        ) : (
                          <Checkbox 
                            checked={isAssigned}
                            className="pointer-events-none"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Summary */}
          <div className="flex items-center justify-between text-sm pt-2 border-t">
            <span className="text-muted-foreground">
              {assignedDriverIds.length} conducteur{assignedDriverIds.length !== 1 ? "s" : ""} assigné{assignedDriverIds.length !== 1 ? "s" : ""}
            </span>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fermer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
